import { useState } from 'react';
import moment from 'moment';
import { useAtom } from 'jotai';
import { useHistory } from 'react-router-dom';
import { Button, InlineLoading } from '@carbon/react';
import { Copy } from '@carbon/react/icons';
import routes from '~/routes';
import {
  ScenarioFormLayout,
  type ToastState,
} from 'components/scenario-form-layout';
import { ScenarioDetailsSection } from 'components/scenario-details-section';
import { TextField, TextAreaField } from 'components/form-fields-2';
import { DatePicker } from 'components/date-picker/date-picker';
import type { ScenarioNavItem } from 'components/scenario-nav/types';
import type { ScenarioMapFeature } from 'components/scenario-map/types';
import {
  areaGeometriesAtom,
  expenseScenarioMapConfigAtom,
  saveScenarioLoadableAtom,
} from './atoms';
import { ExpenseAreaDetails } from './area-details';
import {
  GLOBAL_SELECTION_ID,
  buildCreatePayload,
  buildUpdatePayload,
  hasFallbackAfe,
  isExpenseScenarioValid,
  MISSING_AFE_AREA_ERROR,
  MISSING_AFE_FORM_ERROR,
  type FormMode,
} from './utils';
import type { ExpenseScenarioFormData } from './types';
import type { ExpenseAreaConfig } from '../types';
import { cloneScenarioLoadableAtom } from '../atoms';
import styles from './page.module.scss';

interface ExpenseScenarioFormProps {
  initialValues: ExpenseScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
}

/**
 * Expense scenario form. Initializes local state from initialValues
 * (blank in create mode, mapped from the loaded scenario in edit mode).
 * Reuses the shared ScenarioFormLayout (map + sidebar + content header) and
 * ScenarioDetailsSection (name/date/description + geometry priority).
 * @param {ExpenseScenarioFormProps} props - The form props
 * @return {JSX.Element} The scenario form
 */
export function ExpenseScenarioForm({
  initialValues,
  mode,
  scenarioId,
}: ExpenseScenarioFormProps): JSX.Element {
  const history = useHistory();
  const [saveState, triggerSave] = useAtom(saveScenarioLoadableAtom);
  const [geometries, setGeometries] = useAtom(areaGeometriesAtom);
  const [cloneState, triggerClone] = useAtom(cloneScenarioLoadableAtom);
  const isCloning = cloneState.state === 'loading';

  const [notes, setNotes] = useState(initialValues.notes);
  const [name, setName] = useState(initialValues.name);
  const [effectiveDate, setEffectiveDate] = useState(
    initialValues.effectiveDate
  );
  const [description, setDescription] = useState(initialValues.description);
  const [geometryPriority, setGeometryPriority] = useState<string[]>(
    initialValues.geometryPriority
  );
  const [areas, setAreas] = useState<ExpenseAreaConfig[]>(initialValues.areas);
  const [selectedId, setSelectedId] = useState<number | string>(
    GLOBAL_SELECTION_ID
  );
  const [toast, setToast] = useState<ToastState | null>(null);

  const areaDisplayName = (area: ExpenseAreaConfig) =>
    area.alias || area.areaName || area.unitName || 'New Area';

  const navItems: ScenarioNavItem[] = areas.map((area) => ({
    id: area.id,
    name: areaDisplayName(area),
    badge: area.geometryType === 'unit' ? area.unitType : area.category,
    isNew: !area.areaName && !area.unitName,
    // Flagged in the nav, and summarised beside Save, so a Save blocked by an
    // area not on screen is explained without clicking through each area.
    error: hasFallbackAfe(area) ? undefined : MISSING_AFE_AREA_ERROR,
  }));
  const saveBlockedReason = areas.every(hasFallbackAfe)
    ? undefined
    : MISSING_AFE_FORM_ERROR;

  const selectedArea =
    selectedId === GLOBAL_SELECTION_ID
      ? undefined
      : areas.find((area) => area.id === selectedId);

  // Areas with a fetched geometry render as polygons on the map. Area/unit are
  // the only types with a geometry endpoint today (state/county have none yet).
  const mapFeatures: ScenarioMapFeature[] = areas
    .filter((area) => geometries[area.id])
    .map((area) => ({
      id: area.id,
      geometry: geometries[area.id],
      type: 'polygon' as const,
    }));

  const handleNavAdd = () => {
    const nextId = areas.reduce((max, area) => Math.max(max, area.id), 0) + 1;
    const newArea: ExpenseAreaConfig = {
      id: nextId,
      configurableId: 0,
      geometryType: 'area',
      category: '',
      areaName: '',
      unitType: '',
      unitName: '',
      alias: '',
      notes: '',
      economicLimitApplied: false,
      economicLimitAmount: null,
      economicLimitReactivate: false,
      economicLimitReactivateMonths: null,
      fallbackAfeHeaderId: null,
      fallbackAfe: null,
      lineItems: [],
    };
    setAreas((prev) => [...prev, newArea]);
    setSelectedId(nextId);
  };

  const handleNavDelete = (id: number | string) => {
    setAreas((prev) => prev.filter((area) => area.id !== id));
    setSelectedId((prev) => (prev === id ? GLOBAL_SELECTION_ID : prev));
    if (typeof id === 'number') {
      setGeometries((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const updateArea = (id: number, patch: Partial<ExpenseAreaConfig>) => {
    setAreas((prev) =>
      prev.map((area) => (area.id === id ? { ...area, ...patch } : area))
    );
  };

  const isSaving = saveState.state === 'loading';

  const currentFormData: ExpenseScenarioFormData = {
    name,
    effectiveDate,
    description,
    notes,
    geometryPriority,
    areas,
  };

  // Dirty when the current values diverge from what the form loaded with
  // (blank in create mode, the saved scenario in edit mode).
  const isDirty =
    JSON.stringify(currentFormData) !== JSON.stringify(initialValues);

  // Save is enabled only once every mandatory field is filled and there are
  // unsaved changes to persist.
  const canSave = isDirty && isExpenseScenarioValid(currentFormData);

  const handleCancel = () => {
    history.push(routes.expenseScenariosPath);
  };

  const handleClone = () => {
    if (!scenarioId) return;
    triggerClone(Number(scenarioId))
      .then((data) => {
        history.push(data.href);
      })
      .catch(() => {
        setToast({ message: 'Failed to clone scenario', type: 'error' });
      });
  };

  const handleSave = () => {
    const payload =
      mode === 'create'
        ? buildCreatePayload(currentFormData)
        : buildUpdatePayload(currentFormData, initialValues);

    triggerSave({
      mode,
      id: scenarioId ? Number(scenarioId) : undefined,
      payload,
    })
      .then(() => {
        history.push(routes.expenseScenariosPath);
      })
      .catch((error: unknown) => {
        setToast({
          message:
            error instanceof Error ? error.message : 'Failed to save scenario',
          type: 'error',
        });
      });
  };

  return (
    <ScenarioFormLayout
      title={
        mode === 'edit'
          ? name || 'Edit Expense Scenario'
          : 'New Expense Scenario'
      }
      notes={{ value: notes, onChange: setNotes }}
      mapFeatures={mapFeatures}
      mapFocusedId={typeof selectedId === 'number' ? selectedId : null}
      mapConfigAtom={expenseScenarioMapConfigAtom}
      navItems={navItems}
      navSelectedId={selectedId}
      onNavSelect={setSelectedId}
      onNavAdd={handleNavAdd}
      onNavDelete={handleNavDelete}
      navGlobalLabel="Scenario Details"
      headerActions={
        <div className={styles.headerActions}>
          {mode === 'edit' && (
            <Button
              kind="tertiary"
              size="md"
              renderIcon={Copy}
              disabled={isCloning}
              onClick={handleClone}
            >
              {isCloning ? <InlineLoading /> : 'Clone Scenario'}
            </Button>
          )}
          <Button kind="secondary" size="md" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      }
      contentTitle={
        selectedArea ? areaDisplayName(selectedArea) : 'Scenario Details'
      }
      isDirty={isDirty}
      canSave={canSave}
      isSaving={isSaving}
      onSave={handleSave}
      saveLabel="Save Scenario"
      saveBlockedReason={saveBlockedReason}
      toast={toast}
      onToastClose={() => setToast(null)}
    >
      {selectedArea ? (
        <ExpenseAreaDetails
          area={selectedArea}
          onChange={(patch) => updateArea(selectedArea.id, patch)}
        />
      ) : (
        <ScenarioDetailsSection
          geometryPriorityItems={geometryPriority}
          onGeometryPriorityChange={setGeometryPriority}
          showNameWarning={!name.trim()}
          warningText="A configuration must have a name before it can be saved."
        >
          <div className={styles.fields}>
            <div className={styles.nameRow}>
              <TextField
                id="expense-scenario-name"
                labelText="Configuration Name (required)"
                placeholder="e.g. Permian Basin — 2026 Base"
                value={name}
                onChange={setName}
                light
              />
              <DatePicker
                id="expense-effective-date"
                labelText="Effective Date"
                value={effectiveDate || undefined}
                light
                onChange={(dates: Date[]) =>
                  setEffectiveDate(
                    dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : ''
                  )
                }
              />
            </div>

            <TextAreaField
              id="expense-scenario-description"
              labelText="Description"
              placeholder="Describe the purpose and scope of this expense configuration..."
              rows={6}
              value={description}
              onChange={setDescription}
            />
          </div>
        </ScenarioDetailsSection>
      )}
    </ScenarioFormLayout>
  );
}
